import {
    ActionIcon,
    Alert,
    Box,
    Button,
    Divider,
    Group,
    Loader,
    Modal,
    Paper,
    Popover,
    Stack,
    Table as MantineTable,
    Text,
    Title,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconCloudUpload, IconTrash, IconPencil, IconInfoCircle } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { filesize } from 'filesize';
import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router';

import { PageWide } from '../../components/PageLayout';
import { Table } from '../../components/Table';
import { ThumbnailPreview } from '../../components/ThumbnailPreview';
import type { Game } from '../../data/games';
import {
    requestCreativeFiles,
    uploadCreativeMedia,
    useCreativeMedia,
    deleteCreativeMedia,
    updateCreativeMedia,
    validateCreativeFile,
} from '../../data/media';
import type { CreativeMediaItem } from '../../data/media';
import * as remote from '../../data/remote-data';
import { CREATIVE_LIMITATIONS } from '../../lib/creative-limits';
import { getCurrentMonday } from '../../lib/date-utils';
import { plural } from '../../lib/utils';
import { BulkEditCreativeMediaModal } from '../../platforms/line/components/BulkEditCreativeMediaModal';
import type { BulkEditCreativeMediaFormData } from '../../platforms/line/components/BulkEditCreativeMediaModal';
import { EditCreativeMediaForm } from '../../platforms/line/components/EditCreativeMediaForm';
import type { EditCreativeMediaFormData } from '../../platforms/line/components/EditCreativeMediaForm';

import { MarketingFieldsPopup } from './MarketingFieldsPopup';
import type { PendingCreative } from './MarketingFieldsPopup';

type CreativeRepositoryRow = {
    mediaId: string;
    name: string;
    mimeType: string;
    uploadedAt: string;
    url: string;
    width: number;
    height: number;
};

function formatLimitations(limits: import('../../lib/creative-limits').CreativeLimitations): [string, string][] {
    const rows: [string, string][] = [];
    rows.push(['Max file size', filesize(limits.maxFileSize, { standard: 'jedec' })]);
    if (limits.recommendedSizes) {
        rows.push([
            'Recommended sizes',
            limits.recommendedSizes.map((s) => `${s.width}×${s.height}`).join(', ') + ' px',
        ]);
    }
    if (limits.aspectRatios) {
        rows.push(['Aspect ratios', limits.aspectRatios.map((r) => `${r.width}:${r.height}`).join(', ')]);
    }
    if (limits.maxDuration) {
        rows.push(['Max length', `${limits.maxDuration} seconds`]);
    }
    if (limits.duration) rows.push(['Duration', limits.duration]);
    if (limits.frames) rows.push(['Frames', limits.frames]);
    if (limits.loops) rows.push(['Loops', limits.loops]);
    return rows;
}

const LimitationsSection = ({ label, rows }: { label: string; rows: [string, string][] }) => (
    <Stack gap={4}>
        <Text fw={600} size="sm">
            {label}
        </Text>
        <MantineTable
            withRowBorders={false}
            verticalSpacing={2}
            horizontalSpacing="xs"
            layout="auto"
            styles={{ td: { paddingBlock: 2 } }}
        >
            <MantineTable.Tbody>
                {rows.map(([key, value]) => (
                    <MantineTable.Tr key={key}>
                        <MantineTable.Td w={130}>
                            <Text size="xs" c="dimmed">
                                {key}
                            </Text>
                        </MantineTable.Td>
                        <MantineTable.Td>
                            <Text size="xs">{value}</Text>
                        </MantineTable.Td>
                    </MantineTable.Tr>
                ))}
            </MantineTable.Tbody>
        </MantineTable>
    </Stack>
);

export const CreativeRepositoryTab = () => {
    const { gameId } = useOutletContext<{ game: Game; gameId: string }>();
    const media = useCreativeMedia(gameId);
    const [uploading, setUploading] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [pendingCreatives, setPendingCreatives] = useState<PendingCreative[]>([]);
    const [deletingMediaId, setDeletingMediaId] = useState<string | null>(null);
    const [selectedMediaIds, setSelectedMediaIds] = useState<Set<string | number>>(new Set());
    const [isDeleting, setIsDeleting] = useState(false);
    const [editingMedia, setEditingMedia] = useState<CreativeMediaItem | undefined>(undefined);
    const [showBulkEdit, setShowBulkEdit] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    // Auto-load creative media on mount
    useEffect(() => {
        if (remote.isNotAsked(media.data)) {
            void media.load();
        }
    }, []);

    const data = useMemo<remote.RemoteData<CreativeRepositoryRow[], string>>(() => {
        return remote.map(media.data, (allMedia: CreativeMediaItem[]) => {
            return allMedia.map((mediaItem: CreativeMediaItem) => ({
                mediaId: mediaItem.mediaId,
                name: mediaItem.name,
                mimeType: mediaItem.mimeType,
                uploadedAt: new Date(mediaItem.uploadedAt).toISOString(),
                url: mediaItem.url,
                width: mediaItem.width,
                height: mediaItem.height,
            }));
        });
    }, [media.data]);

    const processFiles = async (files: FileList | File[]) => {
        try {
            if (!files || files.length === 0) {
                return;
            }

            // Validate files
            const validationResults = await Promise.all(
                Array.from(files).map(async (file) => ({
                    file,
                    validation: await validateCreativeFile(file),
                })),
            );

            // Check if any files have errors
            const filesWithErrors = validationResults.filter((r) => r.validation.errors.length > 0);
            if (filesWithErrors.length > 0) {
                return;
            }

            // Get current Monday as default week
            const currentMonday = getCurrentMonday().format('YYYY-MM-DD');

            // Create pending creatives with default marketing fields and validation info
            const creatives: PendingCreative[] = validationResults.map(({ file, validation }) => ({
                file,
                id: crypto.randomUUID(),
                marketingFields: {
                    week: currentMonday,
                    hasTextOverlay: false,
                    hasCallToAction: false,
                    additionalText: '',
                },
                validation,
            }));

            setPendingCreatives(creatives);
            setShowPopup(true);
        } catch (error: any) {
            notifications.show({
                title: 'Processing failed',
                message: error.message || 'Unknown error occurred',
                color: 'red',
            });
        }
    };

    const handleUpload = async () => {
        try {
            const files = await requestCreativeFiles();
            if (files) {
                await processFiles(files);
            }
        } catch (error: any) {
            notifications.show({
                title: 'File selection failed',
                message: error.message || 'Unknown error occurred',
                color: 'red',
            });
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsDragging(false);
        }
    };

    const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (uploading) {
            return;
        }

        const droppedFiles = e.dataTransfer.files;
        if (droppedFiles && droppedFiles.length > 0) {
            const validFiles = Array.from(droppedFiles).filter((file) => {
                return file.type.startsWith('image/') || file.type.startsWith('video/');
            });

            if (validFiles.length === 0) {
                notifications.show({
                    title: 'Invalid files',
                    message: 'Please drop only image or video files.',
                    color: 'red',
                });
                return;
            }

            if (validFiles.length !== droppedFiles.length) {
                const skippedCount = droppedFiles.length - validFiles.length;
                notifications.show({
                    title: 'Some files skipped',
                    message: `Skipped ${skippedCount} non-image/video ${plural(skippedCount, 'file', 'files')}. Only image and video files are supported.`,
                    color: 'yellow',
                });
            }

            await processFiles(validFiles);
        }
    };

    const handlePopupSubmit = async (creatives: PendingCreative[]) => {
        try {
            setUploading(true);

            const result = await uploadCreativeMedia({
                gameId,
                creatives: creatives.map((creative) => ({
                    file: creative.file,
                    marketingFields: creative.marketingFields,
                })),
            });

            notifications.show({ title: 'Success', message: `Uploaded ${result.uploaded} file(s)`, color: 'green' });

            await media.load();
            setShowPopup(false);
        } catch (error: any) {
            notifications.show({
                title: 'Upload failed',
                message: error.message || 'Unknown error occurred',
                color: 'red',
            });
        } finally {
            setUploading(false);
        }
    };

    const handlePopupCancel = () => {
        setShowPopup(false);
        setPendingCreatives([]);
    };

    const handleDelete = (mediaId: string, mediaName: string) => {
        modals.openConfirmModal({
            title: 'Delete media',
            children: (
                <Text>Are you sure you want to delete &ldquo;{mediaName}&rdquo;? This action cannot be undone.</Text>
            ),
            labels: { confirm: 'Delete', cancel: 'Cancel' },
            confirmProps: { color: 'red' },
            onConfirm: async () => {
                try {
                    setDeletingMediaId(mediaId);

                    await deleteCreativeMedia({ gameId, mediaIds: [mediaId] });

                    notifications.show({ title: 'Success', message: `Deleted "${mediaName}"`, color: 'green' });

                    await media.load();
                } catch (error: any) {
                    notifications.show({
                        title: 'Delete failed',
                        message: `Failed to delete "${mediaName}"`,
                        color: 'red',
                    });
                } finally {
                    setDeletingMediaId(null);
                }
            },
        });
    };

    const handleBatchDelete = () => {
        if (selectedMediaIds.size === 0) return;

        modals.openConfirmModal({
            title: 'Delete media',
            children: (
                <Text>
                    Are you sure you want to delete {selectedMediaIds.size} media{' '}
                    {plural(selectedMediaIds.size, 'item', 'items')}? This action cannot be undone.
                </Text>
            ),
            labels: { confirm: 'Delete', cancel: 'Cancel' },
            confirmProps: { color: 'red' },
            onConfirm: async () => {
                setIsDeleting(true);
                try {
                    const mediaIds = Array.from(selectedMediaIds).map((id) => String(id));
                    await deleteCreativeMedia({ gameId, mediaIds });

                    notifications.show({
                        title: 'Success',
                        message: `${mediaIds.length} item(s) deleted`,
                        color: 'green',
                    });

                    setSelectedMediaIds(new Set());
                    await media.load();
                } catch (error: any) {
                    notifications.show({ title: 'Delete failed', message: 'Failed to delete media', color: 'red' });
                } finally {
                    setIsDeleting(false);
                }
            },
        });
    };

    const handleUpdateMedia = async (formData: EditCreativeMediaFormData) => {
        try {
            setIsSubmitting(true);

            const { mediaId, ...updates } = formData;
            await updateCreativeMedia({
                gameId,
                updates: { [mediaId]: updates },
            });

            notifications.show({ title: 'Success', message: 'Media updated', color: 'green' });

            setEditingMedia(undefined);
            await media.load();
        } catch (error: any) {
            notifications.show({
                title: 'Update failed',
                message: error.message || 'Unknown error occurred',
                color: 'red',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBulkUpdate = async (formData: BulkEditCreativeMediaFormData) => {
        try {
            setIsSubmitting(true);

            const mediaIds = Array.from(selectedMediaIds).map((id) => String(id));
            const updates = Object.fromEntries(mediaIds.map((id) => [id, formData]));

            await updateCreativeMedia({ gameId, updates });

            notifications.show({ title: 'Success', message: `${mediaIds.length} item(s) updated`, color: 'green' });

            setShowBulkEdit(false);
            setSelectedMediaIds(new Set());
            await media.load();
        } catch (error: any) {
            notifications.show({
                title: 'Update failed',
                message: error.message || 'Unknown error occurred',
                color: 'red',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <PageWide>
            <Box onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} pos="relative">
                <Stack gap="lg">
                    {/* Title row with Upload button */}
                    <Group justify="space-between" align="center">
                        <Title order={4}>Creative Repository</Title>
                        <Group gap="xs">
                            <Popover width={360} position="bottom-end" shadow="md" withArrow>
                                <Popover.Target>
                                    <ActionIcon variant="subtle" color="gray" aria-label="Upload requirements">
                                        <IconInfoCircle size={18} />
                                    </ActionIcon>
                                </Popover.Target>
                                <Popover.Dropdown>
                                    <Stack gap="xs">
                                        <LimitationsSection
                                            label="Images"
                                            rows={formatLimitations(CREATIVE_LIMITATIONS.image)}
                                        />
                                        <Divider />
                                        <LimitationsSection
                                            label="Videos"
                                            rows={formatLimitations(CREATIVE_LIMITATIONS.video)}
                                        />
                                        <Divider />
                                        <LimitationsSection
                                            label="Animated Images (APNG)"
                                            rows={formatLimitations(CREATIVE_LIMITATIONS['animated-image'])}
                                        />
                                    </Stack>
                                </Popover.Dropdown>
                            </Popover>
                            <Button
                                onClick={handleUpload}
                                disabled={uploading}
                                leftSection={uploading ? <Loader size={16} /> : <IconCloudUpload size={20} />}
                            >
                                {uploading ? 'Uploading...' : 'Upload Creatives'}
                            </Button>
                        </Group>
                    </Group>

                    {/* Data table (no wrapping Card — MRT renders its own Paper) */}
                    {remote.isFailure(media.data) ? (
                        <Alert color="red" title="Failed to load creative media">
                            <Text>{media.data.value}</Text>
                        </Alert>
                    ) : (
                        <Table
                            storageKey="creative-repository"
                            data={remote.withDefault(data, [])}
                            loading={remote.isLoading(media.data) || remote.isNotAsked(media.data)}
                            id={(row) => row.mediaId}
                            defaultSortColumn="Upload Date"
                            pagination={{
                                pageSize: 20,
                                showPagination: true,
                            }}
                            selection={{
                                selectedIds: selectedMediaIds,
                                onSelectionChange: setSelectedMediaIds,
                                actions: [
                                    {
                                        label: 'Edit Selected',
                                        onClick: () => setShowBulkEdit(true),
                                        disabled: isSubmitting,
                                        variant: 'outline',
                                        startIcon: <IconPencil size={16} />,
                                    },
                                    {
                                        label: 'Delete Selected',
                                        onClick: handleBatchDelete,
                                        disabled: isDeleting,
                                        variant: 'filled',
                                        color: 'red',
                                        startIcon: <IconTrash size={16} />,
                                    },
                                ],
                            }}
                            density="xs"
                            columns={[
                                {
                                    name: 'Preview',
                                    alt: 'Media preview',
                                    width: 56,
                                    type: 'thumbnail' as const,
                                    render: (row) => {
                                        const isVideo = row.mimeType.startsWith('video/');

                                        return <ThumbnailPreview url={row.url} isVideo={isVideo} alt={row.name} />;
                                    },
                                },
                                {
                                    name: 'Name',
                                    alt: 'Generated name from marketing fields',
                                    render: (row) => row.name,
                                    sort: (a: CreativeRepositoryRow, b: CreativeRepositoryRow) =>
                                        a.name.localeCompare(b.name),
                                },
                                {
                                    name: 'Upload Date',
                                    alt: 'Date when the media was uploaded',
                                    type: 'date' as const,
                                    width: 200,
                                    render: (row) => dayjs(row.uploadedAt).format('MMM D, YYYY h:mm A'),
                                    sort: (a: CreativeRepositoryRow, b: CreativeRepositoryRow) =>
                                        new Date(a.uploadedAt).getTime() - new Date(b.uploadedAt).getTime(),
                                    defaultDirection: 'desc' as const,
                                },
                                {
                                    name: 'Actions',
                                    alt: 'Actions available for this media item',
                                    type: 'actions' as const,
                                    width: 80,
                                    render: (row) => {
                                        const mediaItem = remote
                                            .withDefault(media.data, [])
                                            .find((m) => m.mediaId === row.mediaId);
                                        return (
                                            <Group justify="flex-end" gap={4}>
                                                <ActionIcon
                                                    variant="subtle"
                                                    onClick={() => mediaItem && setEditingMedia(mediaItem)}
                                                    disabled={isSubmitting}
                                                    aria-label="Edit this media"
                                                >
                                                    <IconPencil size={16} />
                                                </ActionIcon>
                                                <ActionIcon
                                                    variant="subtle"
                                                    color="red"
                                                    onClick={() => handleDelete(row.mediaId, row.name)}
                                                    disabled={deletingMediaId === row.mediaId}
                                                    aria-label="Delete this media"
                                                >
                                                    {deletingMediaId === row.mediaId ? (
                                                        <Loader size={16} />
                                                    ) : (
                                                        <IconTrash size={16} />
                                                    )}
                                                </ActionIcon>
                                            </Group>
                                        );
                                    },
                                },
                            ]}
                        />
                    )}
                </Stack>

                {/* Drag-and-drop overlay */}
                {isDragging && (
                    <Paper
                        pos="absolute"
                        style={{
                            inset: 0,
                            zIndex: 10,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                        bg="light-dark(rgba(255,255,255,0.9), rgba(0,0,0,0.8))"
                        bd="2px dashed blue.6"
                        radius="sm"
                    >
                        <Stack align="center" gap="xs">
                            <IconCloudUpload size={48} color="var(--mantine-color-blue-6)" />
                            <Text fw={600}>Drop files here to upload</Text>
                        </Stack>
                    </Paper>
                )}

                {/* Marketing Fields Popup */}
                <MarketingFieldsPopup
                    creatives={pendingCreatives}
                    onSubmit={handlePopupSubmit}
                    onCancel={handlePopupCancel}
                    isUploading={uploading}
                    opened={showPopup}
                    onClose={handlePopupCancel}
                />

                {/* Edit Creative Media Modal */}
                {editingMedia && (
                    <Modal
                        opened={!!editingMedia}
                        onClose={() => setEditingMedia(undefined)}
                        size="lg"
                        title="Edit Creative Media"
                    >
                        <EditCreativeMediaForm
                            media={editingMedia}
                            onSubmit={handleUpdateMedia}
                            onCancel={() => setEditingMedia(undefined)}
                            isSubmitting={isSubmitting}
                        />
                    </Modal>
                )}

                {/* Bulk Edit Creative Media Modal */}
                {showBulkEdit && (
                    <Modal
                        opened={showBulkEdit}
                        onClose={() => setShowBulkEdit(false)}
                        size="lg"
                        title="Bulk Edit Creative Media"
                    >
                        <BulkEditCreativeMediaModal
                            selectedCount={selectedMediaIds.size}
                            onSubmit={handleBulkUpdate}
                            onCancel={() => setShowBulkEdit(false)}
                            isSubmitting={isSubmitting}
                        />
                    </Modal>
                )}
            </Box>
        </PageWide>
    );
};
